import React, { useState, useEffect, useRef } from 'react';
import { streamChatService } from '../services/streamChatService';
import { supabase } from '../services/supabase';
import { discordAuth } from '../services/discordAuth';
import '../styles/LiveChat.css';

const LiveChat = ({ streamId, username = 'Guest', isAdmin = false, userId = null }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [discordUser, setDiscordUser] = useState(null);
  const [currentUsername, setCurrentUsername] = useState(username);
  const messagesEndRef = useRef(null);
  const syncedMessageIds = useRef(new Set());

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const storedUser = discordAuth.getStoredUser();
    if (storedUser) {
      setDiscordUser(storedUser);
      setCurrentUsername(storedUser.username);
    }
  }, []);

  // Load initial messages
  useEffect(() => {
    if (!streamId) return;

    const loadMessages = async () => {
      try {
        setLoading(true);
        const data = await streamChatService.getMessages(streamId);
        setMessages(data);
        syncedMessageIds.current = new Set(data.map(m => m.id));
      } catch (err) {
        console.error('Error loading messages:', err);
        setError('Failed to load chat');
      } finally {
        setLoading(false);
      }
    };

    loadMessages();
  }, [streamId]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!streamId) return;

    let subscription;

    try {
      subscription = supabase
        .channel(`live_chat:${streamId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'live_chat',
            filter: `stream_id=eq.${streamId}`
          },
          (payload) => {
            if (!syncedMessageIds.current.has(payload.new.id)) {
              syncedMessageIds.current.add(payload.new.id);
              setMessages((prev) => {
                const exists = prev.some(m => m.id === payload.new.id);
                return exists ? prev : [...prev, payload.new];
              });
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'live_chat',
            filter: `stream_id=eq.${streamId}`
          },
          (payload) => {
            syncedMessageIds.current.delete(payload.old.id);
            setMessages((prev) => prev.filter((m) => m.id !== payload.old.id));
          }
        )
        .subscribe((status) => {
          setRealtimeConnected(status === 'SUBSCRIBED');
        });
    } catch (err) {
      console.error('Error subscribing to chat:', err);
    }

    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [streamId]);

  // Fetch viewer count periodically
  useEffect(() => {
    if (!streamId) return;

    let failCount = 0;
    const fetchViewerCount = async () => {
      try {
        const count = await streamChatService.getViewerCount(streamId);
        setViewerCount(count);
        failCount = 0;
      } catch (err) {
        failCount++;
        if (failCount === 1) {
          console.warn('Could not fetch viewer count - Mux Edge Function may not be configured');
        }
        setViewerCount(0);
      }
    };

    fetchViewerCount();
    const interval = setInterval(fetchViewerCount, 5000);

    return () => clearInterval(interval);
  }, [streamId]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const messageText = inputValue;
    const tempId = `temp_${Date.now()}`;
    const optimisticMessage = {
      id: tempId,
      stream_id: streamId,
      username,
      message: messageText,
      is_admin: isAdmin,
      created_at: new Date().toISOString()
    };

    try {
      setSending(true);
      setInputValue('');
      setMessages((prev) => [...prev, optimisticMessage]);

      const response = await streamChatService.sendMessage(
        streamId,
        currentUsername,
        messageText,
        isAdmin,
        userId || (discordUser ? discordUser.id : null)
      );
      
      if (response && response.length > 0) {
        const sentMessage = response[0];
        syncedMessageIds.current.add(sentMessage.id);
        setMessages((prev) =>
          prev.map((msg) => (msg.id === tempId ? sentMessage : msg))
        );
      }
      setError(null);
    } catch (err) {
      console.error('Error sending message:', err);
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
      setError('Failed to send message: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!isAdmin) return;
    if (!window.confirm('Delete this message?')) return;

    try {
      await streamChatService.deleteMessage(messageId);
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('Failed to delete message');
    }
  };

  const handleClearChat = async () => {
    if (!isAdmin) return;
    if (!window.confirm('Clear all chat messages?')) return;

    try {
      await streamChatService.clearChat(streamId);
      setMessages([]);
    } catch (err) {
      console.error('Error clearing chat:', err);
      setError('Failed to clear chat');
    }
  };

  const handleDiscordLogin = () => {
    const authUrl = discordAuth.getAuthorizationUrl();
    window.location.href = authUrl;
  };

  const handleDiscordLogout = () => {
    discordAuth.clearToken();
    setDiscordUser(null);
    setCurrentUsername('Guest');
  };

  if (!discordUser) {
    return (
      <div className="live-chat">
        <div className="chat-header">
          <h3>Live Chat</h3>
          <div className="header-right">
            <div className="viewer-count">
              <span className="viewer-dot"></span>
              {viewerCount} viewers
            </div>
          </div>
        </div>
        <div className="login-required">
          <p>Login with Discord to chat</p>
          <button 
            className="discord-login-btn"
            onClick={handleDiscordLogin}
          >
            🔗 Login with Discord
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="live-chat">
      <div className="chat-header">
        <h3>Live Chat</h3>
        <div className="header-right">
          <div className="viewer-count">
            <span className="viewer-dot"></span>
            {viewerCount} viewers
          </div>
          <div className="user-auth">
            <span className="user-badge">
              {discordUser.username}
            </span>
            <button 
              className="logout-btn"
              onClick={handleDiscordLogout}
              title="Logout from Discord"
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      {error && <div className="chat-error">{error}</div>}

      <div className="messages-container">
        {loading ? (
          <p className="chat-loading">Loading messages...</p>
        ) : messages.length === 0 ? (
          <p className="chat-empty">No messages yet. Start the conversation!</p>
        ) : (
          <>
            {messages.map((msg) => (
              <div key={msg.id} className={`message ${msg.is_admin ? 'admin-message' : ''}`}>
                <div className="message-header">
                  <span className="username">
                    {msg.username}
                    {msg.is_admin && <span className="admin-badge">Admin</span>}
                  </span>
                  <span className="timestamp">
                    {new Date(msg.created_at).toLocaleTimeString()}
                  </span>
                </div>
                <p className="message-text">{msg.message}</p>
                {isAdmin && (
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteMessage(msg.id)}
                    title="Delete this message"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <div className="chat-input-area">
        <input
          type="text"
          className="chat-input"
          placeholder="Type a message..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          disabled={sending}
        />
        <button
          className="send-btn"
          onClick={handleSendMessage}
          disabled={sending || !inputValue.trim()}
        >
          {sending ? 'Sending...' : 'Send'}
        </button>
        {isAdmin && (
          <button
            className="clear-btn"
            onClick={handleClearChat}
            title="Clear all messages"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

export default LiveChat;
